import { ref, watch, type Ref } from 'vue';
import { toast } from '@flash-global66/g-toast';
import { openAlert } from '@flash-global66/g-dialog-alert';

// TYPES
import type { CurrencyWalletType } from '@/types/CurrencyWalletType';
import type {
  StablecoinConfirmationDrawerProps,
  StablecoinConfirmationDrawerEmit,
} from '@/modules/wallet/types/createWalletStablecoinDrawer.type';

// ERRORS
import { ErrorsStablecoin } from '@/modules/wallet/errors/createWallet.error';

// STORES
import { useWalletStore } from '@/stores/wallet';

// SHARED
import { walletService } from '@/shared/common/services/wallet.service';
import { termsService } from '@/shared/common/services/terms.service';
import { getReasonMessage } from '@/shared/common/utils/errorMessages.util';

// COMPOSABLES
import useLoader from '@/composables/useLoader';

export function useCreateWalletStablecoinDrawer(
  props: StablecoinConfirmationDrawerProps,
  emit: StablecoinConfirmationDrawerEmit
) {
  const { showGLoader } = useLoader();
  const walletStore = useWalletStore();

  const stablecoinTermsUrl: Ref<string> = ref('');

  watch(
    () => props.modelValue,
    (isOpen) => {
      if (isOpen) fetchStablecoinTerms();
    }
  );

  async function fetchStablecoinTerms() {
    const result = await termsService.getCompanyTermsByBusinessProcess('STABLECOIN');
    result.fold(
      () => {
        stablecoinTermsUrl.value = '';
      },
      (success) => {
        stablecoinTermsUrl.value = success.data[0]?.url ?? '';
      }
    );
  }

  async function createStablecoinWallet(currency: CurrencyWalletType) {
    showGLoader(true);
    const result = await walletService.associateStablecoin(currency.currencyCode);
    showGLoader(false);

    await result.fold(
      async (failure) => {
        const errorMessage = getReasonMessage({
          failure,
          errorMap: ErrorsStablecoin,
          defaultMessage: 'Ocurrió un error al activar la billetera.',
        });
        toast.error(errorMessage);
      },
      async () => {
        closeDrawer();
        await openAlert({
          type: 'info',
          title: 'Moneda creada con éxito',
          description: `En unos minutos podrás ver los datos de tus redes para recibir ${currency.currencyCode} desde hoy.`,
          primaryText: 'Entendido',
        });
        await walletStore.setWallets();
      }
    );
  }

  async function confirmCreateWallet() {
    if (!props.currency) return;
    await createStablecoinWallet(props.currency);
  }

  function closeDrawer() {
    stablecoinTermsUrl.value = '';
    emit('close');
  }

  return {
    stablecoinTermsUrl,
    confirmCreateWallet,
    closeDrawer,
  };
}
